import React, { useMemo } from 'react';

import { useAppSelector } from '../../../../../app/hooks';
import { GREEN_DROPDOWN_CARET, GREY_DROPDOWN_CARET } from '../../../../../assets/hostedassets';
import { CellRendererProps } from '../../..';
import StatusDropdown, { StatusDropdownOption } from '../../../StatusDropdown/StatusDropdown';
import { COLORS } from '../../../../../utils/constants/colors';
import { EMPTY_OPTION_VALUE, FIELD_PLACEHOLDER } from '../../../FieldTrigger/constants';
import { getHorizontalLabelId } from '../../DataTable.constants';

export interface UpdateOwnerProps {
    row?: any;
    label?: string;
    value?: string;
    ownerType: string;
    headerId?: string;
}

export const OwnerCellRenderer: React.FC<CellRendererProps> = ({ value, row, column, dynamicProps }) => {
    const owners = useAppSelector((state) => state.investor.ownerDropdownOptions);
    const { updateOwner, isPartner, getOwnerDropdownOption, isDisabled, getSelectedOption, isConfigTableModalDropDown, fieldVariant } =
        dynamicProps || {};

    const headerId = column?.field?.split('.')?.[1] || column?.field || '';

    // Memoize the dropdown data preparation
    const ownerDropdownData = useMemo(() => {
        if (owners.length === 0) return {};

        const ownersData = {};
        owners.forEach((owner) => {
            if (!owner.name) return;
            ownersData[owner.userId] = {
                label: owner?.name,
                value: owner.userId,
                backgroundColor: COLORS.LIGHT_BG,
                labelColor: COLORS.BLACK,
                customCaret: GREY_DROPDOWN_CARET,
            };
        });
        return ownersData;
    }, [owners]);

    const getOwnerDropdownOptionById = (id: string): StatusDropdownOption => {
        if (ownerDropdownData[id]) {
            return ownerDropdownData[id];
        } else {
            if (isPartner)
                return {
                    label: FIELD_PLACEHOLDER.EMPTY,
                    value: EMPTY_OPTION_VALUE,
                    backgroundColor: COLORS.WHITE,
                    labelColor: COLORS.BLACK,
                    customCaret: GREY_DROPDOWN_CARET,
                };
            return {
                label: FIELD_PLACEHOLDER.SELECT,
                value: EMPTY_OPTION_VALUE,
                backgroundColor: COLORS.LIGHT_BG,
                labelColor: COLORS.GREY,
                customCaret: GREY_DROPDOWN_CARET,
            };
        }
    };

    // Use the getSelectedOption function from dynamicProps if provided, otherwise fall back to default logic
    const selectedOption = getSelectedOption
        ? getSelectedOption({ row, value, headerId, getOwnerDropdownOptionById, getOwnerDropdownOption })
        : getOwnerDropdownOption?.(row?.owners?.[headerId]) ?? getOwnerDropdownOptionById(value);

    if (isDisabled && selectedOption.value === EMPTY_OPTION_VALUE && !fieldVariant) {
        return <span style={{ color: COLORS.GREY, paddingLeft: '12px' }}>{FIELD_PLACEHOLDER.EMPTY}</span>;
    }

    return (
        <StatusDropdown
            dropdownOptions={
                Object.values(ownerDropdownData).length > 0
                    ? Object.values(ownerDropdownData)
                    : [
                          {
                              label: FIELD_PLACEHOLDER.SELECT,
                              value: EMPTY_OPTION_VALUE,
                              backgroundColor: 'var(--note-background-color)',
                              labelColor: '#B4B4B4',
                              customCaret: GREEN_DROPDOWN_CARET,
                          },
                      ]
            }
            selectedOption={selectedOption}
            selectOption={(option: StatusDropdownOption) => {
                // Find the owner object to get customSortName
                const selectedOwner = owners.find((owner) => owner.userId === option.value);
                const ownerName = selectedOwner?.customSortName || selectedOwner?.name || option.label;

                const payload: UpdateOwnerProps = {
                    row,
                    label: ownerName,
                    value: option.value,
                    ownerType: column.field,
                    headerId: headerId,
                };
                updateOwner?.(payload);
            }}
            disabled={isPartner || isDisabled}
            isConfigTableModalDropDown={isConfigTableModalDropDown}
            fieldVariant={fieldVariant}
            fieldChip={!!fieldVariant}
            labelledBy={fieldVariant ? getHorizontalLabelId(column?.field) : undefined}
        />
    );
};
